import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import React, { useState, useEffect } from "react";

interface ConnectStepProps {
  title?: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  nextLoading?: boolean;
  nextButtonDisabled?: boolean;
  nextButtonLabel?: string;
  noContainerPadding?: boolean;
  persistentScroll?: boolean;
  onClickNext?: () => void;
  /** Step identifier for PostHog analytics (e.g., "init", "permissions", "auth") */
  stepId?: string;
  /** Optional footer content displayed above the next button */
  footer?: React.ReactNode;
}

const ConnectStep: React.FC<ConnectStepProps> = ({
  title,
  description,
  nextLoading,
  nextButtonDisabled,
  nextButtonLabel,
  onClickNext,
  noContainerPadding,
  persistentScroll,
  stepId,
  footer,
  children,
}) => {
  // Delay showing spinner to avoid flash for quick operations
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (nextLoading) {
      const timer = setTimeout(() => setShowSpinner(true), 300);
      return () => clearTimeout(timer);
    } else {
      setShowSpinner(false);
    }
  }, [nextLoading]);

  return (
    <div className="flex flex-col h-full">
      {/* Header section */}
      {(title || description) && (
        <div
          className={cn("flex-shrink-0", !noContainerPadding && "px-5 pt-4")}
        >
          {title && (
            <motion.h1
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
              className="text-2xl font-semibold tracking-tight text-foreground"
            >
              {title}
            </motion.h1>
          )}
          {description && (
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="text-sm text-muted-foreground mt-1.5 leading-relaxed"
            >
              {description}
            </motion.p>
          )}
        </div>
      )}

      {/* Content area */}
      <div
        className={cn(
          "flex-1 min-h-0",
          persistentScroll ? "overflow-y-scroll" : "overflow-y-auto",
          !noContainerPadding && "px-5",
          (title || description) && "pt-4",
          !title && !description && !noContainerPadding && "pt-4",
          // Add bottom padding to prevent content from being hidden by footer gradient
          nextButtonLabel && "pb-4",
        )}
      >
        {children}
      </div>

      {/* Footer section */}
      {nextButtonLabel && (
        <div className="flex-shrink-0 relative mt-auto">
          {/* Gradient fade - subtle hint that content may scroll */}
          <div className="pointer-events-none bg-gradient-to-t from-background via-background/60 to-transparent absolute bottom-full left-0 right-0 h-6" />

          <div className="px-5 pb-5 pt-2 bg-background">
            {footer && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mb-3"
              >
                {footer}
              </motion.div>
            )}
            <Button
              className="w-full"
              size="lg"
              disabled={nextLoading || nextButtonDisabled}
              onClick={onClickNext}
              data-ph-capture-attribute-step={stepId}
            >
              {nextLoading && showSpinner ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                nextButtonLabel
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectStep;
